// frontend/src/pages/LearningPath.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, coy } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { chatAPI, userAPI, modelAPI } from '../utils/api';
import { logoutIfExpired } from '../utils/auth';
import { learningPaths } from '../data/learningPathsData';
import ModelSelector from '../components/ModelSelector';
import Toast from '../components/Toast';
import LoadingSpinner from '../components/LoadingSpinner';
import ApiErrorHandler from '../components/ApiErrorHandler';

const LearningPath = ({ user }) => {
  const { language } = useParams();
  const navigate = useNavigate();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedChapters, setExpandedChapters] = useState([]);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(null);
  const [lessonContent, setLessonContent] = useState('');
  const [learningMessages, setLearningMessages] = useState([]);
  const [askInput, setAskInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAsk, setLoadingAsk] = useState(false);
  const [userProgress, setUserProgress] = useState(() => {
    const savedProgress = localStorage.getItem('accessai-progress');
    return savedProgress ? JSON.parse(savedProgress) : { completedLessons: {} };
  });
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(
    user?.preferredModel || "xiaomi/mimo-v2-flash:free"
  );
  const [modelStats, setModelStats] = useState({});
  const [loadingModels, setLoadingModels] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [showGenerateButton, setShowGenerateButton] = useState(true);
  
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  
  const sidebarRef = useRef(null);
  const messagesEndRef = useRef(null);

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 4000);
  };

  const closeToast = () => {
    setToast({ show: false, message: '', type: '' });
  };

  // Theme persistence
  useEffect(() => {
    const savedTheme = localStorage.getItem('accessai-theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [learningMessages]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  useEffect(() => {
    localStorage.setItem('accessai-progress', JSON.stringify(userProgress));
  }, [userProgress]);

  // Fetch available models
  useEffect(() => {
    if (user) {
      fetchAvailableModels();
    }
  }, [user]);

  const fetchAvailableModels = async () => {
    if (!user) return;
    
    setLoadingModels(true);
    try {
      const response = await modelAPI.getAvailableModels();
      
      if (response.data.success) {
        setAvailableModels(response.data.models || []);
        setModelStats(response.data.modelStats || {});
        
        if (response.data.userPreferredModel) {
          setSelectedModel(response.data.userPreferredModel);
        }
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      showToast('Failed to load AI models', 'error');
    } finally {
      setLoadingModels(false);
    }
  };

  // Update user's preferred model
  const updateModel = async (modelId) => {
    if (!user) return;
    
    try {
      const response = await modelAPI.updateModel(modelId);
      
      if (response.data.success) {
        setSelectedModel(modelId);
        showToast(`Model switched to ${response.data.modelName}`, 'success');
        
        fetchAvailableModels();
      }
    } catch (error) {
      console.error('Error updating model:', error);
      setApiError({
        status: error.response?.status,
        message: error.response?.data?.details || 'Failed to update model',
        data: error.response?.data
      });
    }
  };

  // Handle model switch from error handler
  const handleSwitchModel = async (modelName) => {
    if (!user) return;
    
    // Find model ID by display name
    const model = availableModels.find(m => 
      m.name === modelName || m.displayName === modelName
    );
    
    if (model) {
      await updateModel(model.id);
      setApiError(null);
    }
  };

  if (!learningPaths[language]) {
    navigate('/');
    return null;
  }

  const path = learningPaths[language];

  const toggleChapter = (index) => {
    setExpandedChapters((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const isLessonCompleted = (chapterIndex, lessonIndex) => {
    const lesson = path.chapters[chapterIndex].lessons[lessonIndex];
    const completedLessons = userProgress.completedLessons[language] || [];
    return completedLessons.includes(lesson.id);
  };

  const selectLesson = (chapterIndex, lessonIndex) => {
    const lesson = path.chapters[chapterIndex].lessons[lessonIndex];
    setCurrentChapterIndex(chapterIndex);
    setCurrentLessonIndex(lessonIndex);
    setLessonContent('');
    setShowGenerateButton(true);
    setLearningMessages([]);
    showToast(`Selected: ${lesson.title}. Click "Generate Lesson" to load content.`, 'info');
  };

  const generateLesson = async () => {
    if (currentLessonIndex === null) {
      showToast('Please select a lesson first', 'warning');
      return;
    }

    if (!user) {
      showToast('Please login to access lessons', 'warning');
      navigate('/login');
      return;
    }

    if (logoutIfExpired(user)) {
      showToast('Your session has expired. Please login again.', 'warning');
      navigate('/login');
      return;
    }

    const lesson = path.chapters[currentChapterIndex].lessons[currentLessonIndex];
    setShowGenerateButton(false);
    setLoading(true);
    
    setLessonContent(
      `## ⏳ Generating lesson content...\n\nPreparing lesson on **${lesson.title}** for **${language}**...\n\nPlease wait while we create the learning materials.`
    );

    const prompt = `Provide a detailed lesson on "${lesson.title}" for ${language} in Markdown format. Include:

- A brief introduction to the topic
- Key concepts with examples
- A coding challenge or quiz for the user to solve
- An explanation of the solution

If the language is Solidity, include blockchain-specific context.`;

    try {
      const response = await chatAPI.sendMessage({
        messages: [],
        input: prompt,
        model: selectedModel,
      });

      setLessonContent(response.data.response);
      showToast('Lesson generated successfully!', 'success');
    } catch (error) {
      console.error('Error generating lesson:', error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        logoutIfExpired(user);
        setApiError({
          status: error.response?.status,
          message: 'Session expired. Please login again.',
          data: error.response?.data
        });
        navigate('/login');
      } else if (error.response?.status === 429) {
        setApiError({
          status: error.response?.status,
          message: error.response?.data?.details || error.message,
          data: error.response?.data
        });
      } else {
        setApiError({
          status: error.response?.status,
          message: 'Failed to generate lesson content. Please try again.',
          data: error.response?.data
        });
        setLessonContent(
          `## ⚠️ Service Unavailable\n\nWe're having trouble generating the lesson content.\n\n**Please try:**\n1. Check your internet connection\n2. Try a different AI model\n3. Wait a few minutes and try again\n\n*If this continues, contact support.*`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAskSubmit = async () => {
    if (!askInput.trim()) return;

    if (!user) {
      showToast('Please login to ask questions', 'warning');
      return;
    }

    if (logoutIfExpired(user)) {
      showToast('Your session has expired. Please login again.', 'warning');
      navigate('/login');
      return;
    }

    const newMessages = [
      ...learningMessages,
      {
        role: 'user',
        content: askInput,
        timestamp: new Date().toLocaleTimeString(),
      },
    ];

    setLearningMessages(newMessages);
    setAskInput('');
    setLoadingAsk(true);

    const enhancedPrompt = `${askInput}. Remember, if asking for direct solution to challenge, encourage trying first.`;

    try {
      const response = await chatAPI.sendMessage({
        messages: newMessages,
        input: enhancedPrompt,
        model: selectedModel,
      });

      const botMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toLocaleTimeString(),
      };

      setLearningMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Error asking question:', error);
      
      if (error.response?.status === 429) {
        setApiError({
          status: error.response?.status,
          message: error.response?.data?.details || error.message,
          data: error.response?.data
        });
      } else {
        setApiError({
          status: error.response?.status,
          message: 'Failed to get response. Please try again.',
          data: error.response?.data
        });
      }
      
      setLearningMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Error: Failed to get response. Please try again.',
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } finally {
      setLoadingAsk(false);
    }
  };

  const markComplete = async (chapterIndex, lessonIndex) => {
    if (!user) {
      showToast('Please login to mark lessons complete', 'warning');
      return;
    }

    const lesson = path.chapters[chapterIndex].lessons[lessonIndex];

    // Update local progress
    setUserProgress((prev) => {
      const completed = prev.completedLessons[language] || [];
      if (!completed.includes(lesson.id)) {
        completed.push(lesson.id);
      }
      return {
        ...prev,
        completedLessons: { ...prev.completedLessons, [language]: completed },
      };
    });

    // Update points on server
    try {
      await userAPI.updatePoints(10, `completed_${lesson.id}`);
      showToast(`Lesson marked complete! +10 XP`, 'success');
    } catch (error) {
      console.error('Error updating points:', error);
      showToast('Lesson marked complete, but failed to update points', 'warning');
    }
  };

  const calculateProgress = () => {
    const allLessons = path.chapters.reduce((acc, chapter) => {
      return [...acc, ...chapter.lessons.map(lesson => lesson.id)];
    }, []);

    const completedLessons = userProgress.completedLessons[language] || [];
    const completed = allLessons.filter(id => completedLessons.includes(id)).length;
    
    return {
      completed,
      total: allLessons.length,
      percentage: allLessons.length > 0 ? Math.round((completed / allLessons.length) * 100) : 0
    };
  };

  const progress = calculateProgress();

  return (
    <div className="app-container">
      {/* Header - Responsive */}
      <header className="header">
        <div className="header-left">
          <button
            onClick={() => navigate('/')}
            className="btn btn-secondary btn-sm text-xs md:text-sm"
          >
            ← Back to Chat
          </button>
          <h1 className="app-title ml-2 md:ml-4 truncate">
            {language.charAt(0).toUpperCase() + language.slice(1)} Learning Path
          </h1>
        </div>
        
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="btn btn-secondary lg:hidden p-2"
          aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          {sidebarOpen ? '✕' : '☰'}
        </button>
      </header>

      <div className="app-layout">
        {/* Sidebar - Responsive */}
        <aside
          ref={sidebarRef}
          className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}
        >
          <div className="sidebar-header">
            <h2 className="text-lg md:text-xl">Chapters</h2>
            <button
              onClick={() => setSidebarOpen(false)}
              className="btn btn-secondary btn-sm lg:hidden p-1.5"
              aria-label="Close sidebar"
            >
              ✕
            </button>
          </div>

          {/* Progress Tracker */}
          <div className="card mb-3 md:mb-4">
            <div className="progress-container">
              <div className="progress-label text-xs md:text-sm">
                <span>Progress</span>
                <span>{progress.percentage}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
            </div>
            <div className="text-xs text-text-muted text-center mt-1.5">
              {progress.completed}/{progress.total} lessons completed
            </div>
          </div>

          {/* Model Selector */}
          {user && (
            <div className="card mb-3 md:mb-4">
              <ModelSelector
                user={user}
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
                onModelStatsUpdate={setModelStats}
              />
            </div>
          )}

          {/* Chapters List - Responsive */}
          <div className="space-y-2">
            {path.chapters.map((chapter, chapIdx) => (
              <div key={chapIdx} className="card p-2 md:p-3">
                <button
                  onClick={() => toggleChapter(chapIdx)}
                  className="w-full text-left flex justify-between items-center"
                >
                  <span className="font-medium text-sm md:text-base truncate">{chapter.title}</span>
                  <span className="text-lg">{expandedChapters.includes(chapIdx) ? '−' : '+'}</span>
                </button>
                
                {expandedChapters.includes(chapIdx) && (
                  <div className="mt-1.5 md:mt-2 space-y-0.5 md:space-y-1">
                    {chapter.lessons.map((lesson, lessIdx) => (
                      <button
                        key={lessIdx}
                        onClick={() => selectLesson(chapIdx, lessIdx)}
                        className={`w-full text-left p-1.5 md:p-2 rounded text-xs md:text-sm flex justify-between items-center truncate ${
                          currentChapterIndex === chapIdx && currentLessonIndex === lessIdx
                            ? 'bg-primary-light text-primary'
                            : 'hover:bg-surface-elevated'
                        } ${
                          isLessonCompleted(chapIdx, lessIdx)
                            ? 'border-l-2 border-success'
                            : ''
                        }`}
                      >
                        <span className="truncate">{lesson.title}</span>
                        {isLessonCompleted(chapIdx, lessIdx) && (
                          <span className="text-success text-sm">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="sidebar-overlay lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content - Responsive */}
        <main className="main-content">
          {currentLessonIndex !== null ? (
            <>
              {/* Current Model Info */}
              {selectedModel && modelStats[selectedModel] && (
                <div className="card mb-4 md:mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm md:text-base font-medium">Current AI Model</h4>
                      <p className="text-xs text-text-muted">
                        Using: {modelStats[selectedModel]?.displayName}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs md:text-sm font-medium">
                        Requests: {modelStats[selectedModel]?.usedToday || 0}/
                        {modelStats[selectedModel]?.dailyLimit || 0}
                      </div>
                      <div className="w-32 bg-border h-1.5 rounded-full mt-1 overflow-hidden">
                        <div
                          className="bg-primary h-full rounded-full"
                          style={{
                            width: `${Math.min(100,
                              ((modelStats[selectedModel]?.usedToday || 0) /
                              (modelStats[selectedModel]?.dailyLimit || 1)) * 100)}%`
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Lesson Content - Responsive */}
              <div className="card mb-4 md:mb-6">
                <div className="flex flex-col xs:flex-row xs:justify-between xs:items-center gap-2 mb-3 md:mb-4">
                  <h2 className="text-lg md:text-xl lg:text-2xl font-bold truncate">
                    {path.chapters[currentChapterIndex].lessons[currentLessonIndex].title}
                  </h2>
                  <div className="flex gap-1.5 md:gap-2">
                    {showGenerateButton && (
                      <button
                        onClick={generateLesson}
                        disabled={loading}
                        className="btn btn-primary btn-sm text-xs md:text-sm py-1.5"
                      >
                        {loading ? 'Generating...' : '📚 Generate Lesson'}
                      </button>
                    )}
                    <button
                      onClick={() => markComplete(currentChapterIndex, currentLessonIndex)}
                      className="btn btn-success btn-sm text-xs md:text-sm py-1.5"
                      disabled={isLessonCompleted(currentChapterIndex, currentLessonIndex)}
                    >
                      {isLessonCompleted(currentChapterIndex, currentLessonIndex) ? '✓ Completed' : 'Mark Complete'}
                    </button>
                    <span className="badge badge-primary text-xs md:text-sm py-1.5">
                      {path.chapters[currentChapterIndex].lessons[currentLessonIndex].difficulty}
                    </span>
                  </div>
                </div>

                {loading ? (
                  <div className="py-6 md:py-8">
                    <LoadingSpinner text="Generating lesson..." />
                  </div>
                ) : (
                  lessonContent && (
                    <div className="prose max-w-none text-sm md:text-base">
                      <ReactMarkdown
                        components={{
                          code({ node, inline, className, children, ...props }) {
                            const match = /language-(\w+)/.exec(className || '');
                            return !inline && match ? (
                              <div className="code-block my-2 md:my-4">
                                <div className="code-header">
                                  <span className="code-language text-xs">{match[1]}</span>
                                  <button
                                    onClick={() => navigator.clipboard.writeText(String(children))}
                                    className="btn btn-secondary btn-sm text-xs"
                                  >
                                    Copy
                                  </button>
                                </div>
                                <div className="code-content">
                                  <SyntaxHighlighter
                                    style={vscDarkPlus}
                                    language={match[1]}
                                    PreTag="div"
                                    customStyle={{ fontSize: '0.75rem', padding: '0.5rem' }}
                                    {...props}
                                  >
                                    {String(children).replace(/\n$/, '')}
                                  </SyntaxHighlighter>
                                </div>
                              </div>
                            ) : (
                              <code className={className} {...props}>
                                {children}
                              </code>
                            );
                          },
                        }}
                      >
                        {lessonContent}
                      </ReactMarkdown>
                    </div>
                  )
                )}
              </div>

              {/* Ask AI Section - Responsive */}
              <div className="card">
                <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">Ask AI for Explanation</h3>
                
                {/* Messages - Responsive */}
                <div className="message-container mb-3 md:mb-4 max-h-64 md:max-h-96 overflow-y-auto">
                  {learningMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`message ${
                        msg.role === 'user' ? 'message-user' : 'message-bot'
                      }`}
                    >
                      <ReactMarkdown
                        components={{
                          code({ node, inline, className, children, ...props }) {
                            const match = /language-(\w+)/.exec(className || '');
                            return !inline && match ? (
                              <code className={`language-${match[1]} bg-code-bg text-code-text px-1 py-0.5 rounded text-xs`} {...props}>
                                {children}
                              </code>
                            ) : (
                              <code className={className} {...props}>
                                {children}
                              </code>
                            );
                          },
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                      <div className="message-timestamp text-[10px] md:text-xs">
                        {msg.timestamp}
                      </div>
                    </div>
                  ))}
                  
                  {loadingAsk && (
                    <div className="message message-bot">
                      <LoadingSpinner size="sm" text="Thinking..." />
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input - Responsive */}
                <div className="flex gap-1.5 md:gap-2">
                  <input
                    value={askInput}
                    onChange={(e) => setAskInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAskSubmit()}
                    placeholder="Ask about this lesson..."
                    className="input-field flex-1 text-xs md:text-sm"
                    disabled={loadingAsk}
                  />
                  <button
                    onClick={handleAskSubmit}
                    disabled={loadingAsk || !askInput.trim()}
                    className="btn btn-primary text-xs md:text-sm py-1.5 md:py-2 px-3 md:px-4"
                  >
                    {loadingAsk ? <LoadingSpinner size="sm" /> : 'Ask'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            /* Welcome Screen - Responsive */
            <div className="empty-state">
              <div className="empty-state-icon">📚</div>
              <h2 className="empty-state-title">
                {language.charAt(0).toUpperCase() + language.slice(1)} Learning Path
              </h2>
              <p className="empty-state-description mb-4 md:mb-8 text-sm md:text-base">
                Select a lesson from the sidebar to start learning
              </p>
              
              {/* Progress Overview - Responsive */}
              <div className="card max-w-xs xs:max-w-sm sm:max-w-md mx-auto">
                <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Your Progress</h3>
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <div className="progress-label text-xs md:text-sm">
                      <span>Overall Progress</span>
                      <span>{progress.percentage}%</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${progress.percentage}%` }}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 md:gap-4">
                    <div className="text-center p-2 md:p-3 bg-surface-elevated rounded-lg">
                      <div className="text-xl md:text-2xl font-bold text-primary">{progress.completed}</div>
                      <div className="text-xs md:text-sm text-text-secondary">Lessons Completed</div>
                    </div>
                    <div className="text-center p-2 md:p-3 bg-surface-elevated rounded-lg">
                      <div className="text-xl md:text-2xl font-bold text-secondary">{progress.total}</div>
                      <div className="text-xs md:text-sm text-text-secondary">Total Lessons</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Next Lesson Suggestion */}
              {progress.completed < progress.total && (
                <button
                  onClick={() => {
                    for (let chapIdx = 0; chapIdx < path.chapters.length; chapIdx++) {
                      for (let lessIdx = 0; lessIdx < path.chapters[chapIdx].lessons.length; lessIdx++) {
                        if (!isLessonCompleted(chapIdx, lessIdx)) {
                          selectLesson(chapIdx, lessIdx);
                          return;
                        }
                      }
                    }
                  }}
                  className="btn btn-primary mt-4 md:mt-6 text-sm md:text-base py-2 md:py-2.5"
                >
                  Continue Learning
                </button>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Toast Notifications */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}

      {/* Global API Error Handler */}
      <ApiErrorHandler
        error={apiError}
        onClose={() => setApiError(null)}
        onSwitchModel={handleSwitchModel}
      />
    </div>
  );
};

export default LearningPath;